import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  message,
  Alert,
  Button,
  Col,
  Form,
  Icon,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Upload
} from 'antd'
import API from '../../../../api/Http'
import * as EndPoints from '../../../../api/EndPoints'
import { ButtonEx } from '../../../../styles/button.style'
import { fetchCategories } from '../../../../modules/category'
import { promoteCustomMaterial } from '../../../../modules/materialCustom'
import {
  beforeUpload,
  compressFile,
  getBase64
} from '../../../../utils/services'

const CONFIRM_TEXT =
  'วัสดุนี้จะปรากฏในรายการกลางและทุกร้านเลือกได้ ร้านเจ้าของยังเห็นชื่อเดิมของตัวเอง การยกขึ้นย้อนกลับไม่ได้'

// ยกวัสดุที่ร้านสร้างขึ้นเป็นวัสดุมาตรฐาน (spec 017 D6–D8) — ใช้วัสดุตัวเดิม แก้หน่วยไม่ได้
const PromoteForm = ({ visible, row, onClose, form }) => {
  const dispatch = useDispatch()
  const { getFieldDecorator, validateFields } = form
  const [imageUrl, setImageUrl] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [error, setError] = useState('')
  const [isSaving, setSaving] = useState(false)

  const categories = useSelector((state) => state.category.categories.data, [])

  useEffect(() => {
    dispatch(fetchCategories())
  }, [dispatch])

  useEffect(() => {
    if (visible && row) {
      form.resetFields()
      form.setFieldsValue({
        name: row.name,
        categoryId: row.categoryId,
        co2: row.co2,
        advance: true,
        allowCustomChild: false
      })
      setImageUrl(row.productImage || '')
      setImageFile(null)
      setError('')
    }
    // ตั้งค่าใหม่เฉพาะเมื่อเปิดฟอร์มของวัสดุคนละตัว
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, row])

  const uploadImage = async (file) => {
    const formData = new FormData()
    formData.append('image', await compressFile(file))
    const response = await API.post(`${EndPoints.MATERAIAL}/image`, formData)
    return response.data
  }

  const promote = async (values) => {
    setSaving(true)
    setError('')
    const data = {
      id: row.id,
      name: values.name.trim(),
      commonName: values.commonName || undefined,
      engName: values.engName || undefined,
      commonEngName: values.commonEngName || undefined,
      categoryId: values.categoryId,
      advance: !!values.advance,
      allowCustomChild: !!values.allowCustomChild
    }
    if (values.co2 !== undefined && values.co2 !== null && values.co2 !== '') {
      data.co2 = values.co2
    }
    try {
      if (imageFile) {
        const image = await uploadImage(imageFile)
        data.productImage = image.id
      }
    } catch (uploadError) {
      setSaving(false)
      setError('อัปโหลดรูปไม่สำเร็จ กรุณาลองใหม่')
      return
    }

    const result = await dispatch(promoteCustomMaterial(data))
    setSaving(false)
    if (!result.success) {
      // backend ปฏิเสธ (เช่น ชื่อซ้ำกับวัสดุมาตรฐานอื่น) — แสดงเหตุผลและไม่ปิดฟอร์ม
      setError(result.message)
      return
    }
    message.success(`ยก "${data.name}" เป็นวัสดุมาตรฐานแล้ว`)
    onClose(true)
  }

  const onSubmit = (e) => {
    e.preventDefault()
    validateFields((err, values) => {
      if (err) {
        return
      }
      Modal.confirm({
        title: `ยก "${values.name.trim()}" เป็นวัสดุมาตรฐาน`,
        content: CONFIRM_TEXT,
        okText: 'ยืนยัน',
        cancelText: 'ยกเลิก',
        onOk: () => promote(values)
      })
    })
  }

  const onChangeImage = (info) => {
    getBase64(info.file, (url) => setImageUrl(url))
  }

  return (
    <Modal
      title="ยกเป็นวัสดุมาตรฐาน"
      visible={visible}
      footer={null}
      maskClosable={false}
      onCancel={() => onClose(false)}>
      {row && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`${
            row.ownerHeroName || `Hero ${row.ownerHeroId}`
          } สร้างไว้ · ตอนนี้ผูกกับ ${row.parentMaterialName || '-'}`}
        />
      )}
      <Form onSubmit={onSubmit}>
        <Form.Item label="รูปหน้าปก" extra="ไม่เลือกรูปใหม่ = ใช้รูปเดิม">
          <Row type="flex" justify="center">
            <Col>
              <Upload
                name="img"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={(file) => beforeUpload(file, setImageFile)}
                onChange={onChangeImage}>
                {imageUrl ? (
                  <img src={imageUrl} alt="img" style={{ width: '100%' }} />
                ) : (
                  <div>
                    <Icon type="plus" />
                    <div className="ant-upload-text">Upload</div>
                  </div>
                )}
              </Upload>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label="ชื่อวัสดุรีไซเคิล">
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                whitespace: true,
                message: 'กรุณากรอก'
              }
            ]
          })(<Input placeholder="ชื่อวัสดุรีไซเคิล" />)}
        </Form.Item>

        <Form.Item label="ชื่ออื่นๆ">
          {getFieldDecorator('commonName')(<Input placeholder="ชื่ออื่นๆ" />)}
        </Form.Item>

        <Form.Item label="English Name">
          {getFieldDecorator('engName')(<Input placeholder="English Name" />)}
        </Form.Item>

        <Form.Item label="Common English Name">
          {getFieldDecorator('commonEngName')(
            <Input placeholder="Common English Name" />
          )}
        </Form.Item>

        <Form.Item label="กลุ่มวัสดุ">
          {getFieldDecorator('categoryId', {
            rules: [
              {
                required: true,
                message: 'กรุณาเลือก'
              }
            ]
          })(
            <Select placeholder="เลือกกลุ่มวัสดุ">
              {(categories || []).map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item
          label="หน่วย"
          extra="แก้หน่วยไม่ได้ เพราะคลังและบิลของร้านเจ้าของบันทึกด้วยหน่วยนี้อยู่แล้ว">
          <Input value={row ? row.unit : ''} disabled />
        </Form.Item>

        <Form.Item label="CO2">
          {getFieldDecorator('co2')(
            <InputNumber style={{ width: '100%' }} min={0} />
          )}
        </Form.Item>

        <Form.Item
          label="ประเภท"
          extra="ขั้นสูง = ยังไม่ขึ้นในรายการเริ่มต้นของแอปผู้ใช้">
          {getFieldDecorator('advance', { initialValue: true })(
            <Radio.Group>
              <Radio value={false}>ทั่วไป</Radio>
              <Radio value>ขั้นสูง</Radio>
            </Radio.Group>
          )}
        </Form.Item>

        <Form.Item
          label="เปิดให้ร้านสร้างสินค้าย่อย"
          extra="ร้านจะสร้างสินค้าของตัวเองใต้วัสดุนี้ได้ เช่น รุ่นโทรศัพท์ หรือกลุ่ม 'อื่นๆ'">
          {getFieldDecorator('allowCustomChild', { initialValue: false })(
            <Radio.Group>
              <Radio value>ใช่</Radio>
              <Radio value={false}>ไม่</Radio>
            </Radio.Group>
          )}
        </Form.Item>

        {error && (
          <Alert
            style={{ marginBottom: 16 }}
            type="error"
            showIcon
            message={error}
          />
        )}

        <Row type="flex" gutter={8} justify="end">
          <Col>
            <Button onClick={() => onClose(false)} disabled={isSaving}>
              ยกเลิก
            </Button>
          </Col>
          <Col>
            <ButtonEx
              color="#6ebf4b"
              type="primary"
              htmlType="submit"
              loading={isSaving}>
              ยกเป็นวัสดุมาตรฐาน
            </ButtonEx>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

PromoteForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired
}

PromoteForm.defaultProps = {
  row: null
}

export default Form.create({ name: 'PromoteCustomMaterialForm' })(PromoteForm)
